/**
 * 举报类型饼图组件
 * 遵循 Opinion 图表开发规范
 */
import React, { useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import { DBO_V } from '@op/utils/format';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import useLanguage from '@op/hooks/useLanguage';
import { getPercentageInfo } from '@op/utils';
import { CATEGORY_CONFIG } from '../../../constants';
import type { ICategoryMentionsTrendResponse } from '@op/services/weeklyReport';

// 分类列表项类型
type TCategoryListItem = ICategoryMentionsTrendResponse['list'][number];

// 默认颜色数组（用于其他类型）
const DEFAULT_COLORS = ['#5086F3', '#69D19C', '#6E7794', '#F5D242', '#7F6FF1', '#5EC8F1'];

// 颜色加深函数
const darkenColor = (hex: string, factor: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const newR = Math.round(r * (1 - factor));
  const newG = Math.round(g * (1 - factor));
  const newB = Math.round(b * (1 - factor));

  return `rgb(${newR}, ${newG}, ${newB})`;
};

interface IReportTypePieChartProps {
  data?: TRequestBackType;
  selectedType: string | null;
  onChartClick?: (params: any) => void;
  isEn?: boolean;
  list?: TCategoryListItem[] | null;
}

const ReportTypePieChart: React.FC<IReportTypePieChartProps> = ({
  data,
  selectedType,
  onChartClick,
  isEn = true,
  list = null,
}) => {
  const { formatMessage } = useLanguage();
  const eChartsRef = useRef<any>(null);

  // 获取颜色 - 使用 CATEGORY_CONFIG
  const getColor = (name: string, index: number): string => {
    return CATEGORY_CONFIG[name]?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  };

  // 获取显示名称 - 根据 isEn 返回英文或中文名
  const getDisplayName = (nameEn: string): string => {
    if (isEn) return nameEn;
    const item = list?.find(i => i.category_name_en === nameEn);
    return item?.category_name || nameEn;
  };

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    const legendData = data.legend || [];
    const series = data.series || [];

    // 计算总数
    const total = series.reduce((sum, item) => sum + (item.data[0] || 0), 0);

    const pieData = series.map((item, index) => {
      const isSelected = selectedType === item.name;
      const baseColor = getColor(item.name, index);
      const value = item.data[0] || 0;

      // 未选中时颜色变淡
      const displayColor = selectedType && !isSelected ? `${baseColor}50` : baseColor;

      return {
        name: item.name,
        value,
        itemStyle: {
          color: displayColor,
          borderColor: isSelected ? darkenColor(baseColor, 0.3) : 'transparent',
          borderWidth: isSelected ? 3 : 0,
        },
      };
    });

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          formatChartParamsDate({
            params: [params],
            listTitleFunc: (item: any) => getDisplayName(item.name),
            tablesConfigFunc: (records: any) => [{
              showHeader: true,
              records: records.map((r: any) => ({
                ...r,
                value: `${DBO_V.reviews(r.originValue)} (${params.percent.toFixed(2)}%)`,
              })),
              columns: [
                { key: 'text', header: formatMessage({ id: 'Report Type' }) },
                { key: 'value', align: 'right', weight: 'bold', header: formatMessage({ id: 'Mentions' }) },
              ],
            }],
          }),
      },
      legend: {
        data: legendData.map((name, index) => ({
          name,
          itemStyle: { color: getColor(name, index) },
        })),
        bottom: 0,
        type: 'scroll',
        formatter: (name: string) => getDisplayName(name),
      },
      title: {
        text: `{name|${DBO_V.reviews(total)}}`,
        top: '40%',
        left: 'center',
        textStyle: {
          rich: {
            name: {
              fontSize: 24,
              fontFamily: 'num-m',
              color: '#000000',
              padding: [10, 0],
            },
          },
        },
      },
      series: [{
        type: 'pie', 
        data: pieData,
        selectedMode: false,
        label: {
          show: true,
          color: '#262832',
          alignTo: 'edge',
          edgeDistance: 10,
          formatter: (params: any) => {
            const displayName = getDisplayName(params.name);
            const percent = getPercentageInfo(params.value, total, 2);
            const percentText = params.value ? `${DBO_V.reviews(params.value)}(${percent})` : '-';
            return `{value| ${displayName}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
          },
          rich: {
            value: {
              lineHeight: 20,
              align: 'center',
            },
            a: {
              lineHeight: 20,
              align: 'center',
            },
            hrLine: {
              borderColor: 'auto',
              width: '105%',
              borderWidth: 0.5,
              height: 0.5,
            },
            per: {
              padding: [4, 0],
            },
          },
        },
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return {
            labelLinePoints: points,
          };
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10,
        },
        emphasis: {
          focus: 'self',
          label: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
        blur: {
          label: { opacity: 0.2 },
          itemStyle: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
      }],
    };
  }, [data, selectedType, formatMessage, isEn, list]);

  const eventsDict = useMemo(() => {
    const events: Record<string, Function> = {};
    if (onChartClick) {
      events.click = onChartClick;
    }
    return events;
  }, [onChartClick]);

  return (
    <EChartCommon
      ref={eChartsRef}
      options={option}
      height={350}
      optionsProps={{ type: 'pie', pieLabelAlignTo: 'edge' }}
      eventsDict={eventsDict}
    />
  );
};

export default ReportTypePieChart;
